import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER = ["match_day", "training", "cancellations", "match_report"];
const LABELS = {
  match_day: "Match day",
  training: "Training",
  cancellations: "Rain-outs",
  match_report: "Report ready",
};
const HELP = {
  match_day: "League fixture reminders for this team",
  training: "The day-before training reminder",
  cancellations: "Rain and haze call-offs",
  match_report: "Sends the announcement when a report for this team is published",
};

export const TeamNotifications = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/whatsapp/team-notifications`,
        { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the team switches");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flip = async (team, channel, value) => {
    setSaving(`${team.id}:${channel}`);
    // Optimistic: the switch answers straight away, and rolls back on failure.
    setData((d) => ({
      ...d,
      teams: d.teams.map((t) =>
        t.id === team.id ? { ...t, switches: { ...t.switches, [channel]: value } } : t),
    }));
    try {
      await axios.put(`${API}/admin/whatsapp/team-notifications/${team.id}`,
        { [channel]: value }, { withCredentials: true });
      toast.success(`${LABELS[channel]} ${value ? "on" : "off"} for ${team.name}`);
    } catch (e) {
      setData((d) => ({
        ...d,
        teams: d.teams.map((t) =>
          t.id === team.id ? { ...t, switches: { ...t.switches, [channel]: !value } } : t),
      }));
      toast.error(e.response?.data?.detail || "Could not save that");
    } finally {
      setSaving("");
    }
  };

  if (!data) {
    return (
      <Card className="p-6 bg-slate-900/50" data-testid="team-notifications-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </Card>
    );
  }

  const needle = query.trim().toLowerCase();
  const teams = data.teams.filter((t) => !needle || t.name.toLowerCase().includes(needle));
  const academy = teams.filter((t) => t.group === "academy");
  const section = teams.filter((t) => t.group === "section");

  const row = (team) => (
    <div
      key={team.id}
      className="flex flex-col gap-3 rounded-lg border border-slate-700/60 bg-slate-800/40 p-4
                 lg:flex-row lg:items-center lg:justify-between"
      data-testid={`team-notif-row-${team.id}`}
    >
      <div className="min-w-[180px]">
        <p className="text-sm font-semibold text-white">{team.name}</p>
        <p className="flex items-center gap-1 text-xs text-slate-400">
          <Users className="w-3 h-3" />
          <span data-testid={`team-notif-players-${team.id}`}>
            {team.players} {team.players === 1 ? "player" : "players"}
          </span>
          {team.age_group ? ` · ${team.age_group}` : ""}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {ORDER.map((channel) => (
          <div key={channel} className="flex items-center gap-2" title={HELP[channel]}>
            <Switch
              checked={team.switches[channel]}
              disabled={saving === `${team.id}:${channel}`}
              onCheckedChange={(v) => flip(team, channel, v)}
              data-testid={`team-switch-${team.id}-${channel}`}
            />
            <span className={`text-xs ${team.switches[channel] ? "text-slate-200" : "text-slate-500"}`}>
              {LABELS[channel]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Card className="p-6 bg-slate-900/50" data-testid="team-notifications-card">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-semibold text-white">WhatsApp by team</p>
          <p className="mt-1 max-w-2xl text-sm text-slate-400">
            Every team starts switched on. Turn one off here and that team's families stop
            getting that message, without touching anyone else. Anything not tied to a team
            keeps sending.
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a team"
            className="w-48 pl-8"
            data-testid="team-notif-search"
          />
        </div>
      </div>

      {!data.whatsapp_enabled && (
        <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
             data-testid="team-notif-whatsapp-off">
          <p className="text-sm text-amber-300">
            WhatsApp itself is switched off in Credentials, so nothing sends to any team yet.
          </p>
        </div>
      )}
      {(data.global_off || []).length > 0 && (
        <div className="mb-4 rounded-lg border border-slate-600/60 bg-slate-800/60 p-3"
             data-testid="team-notif-global-off">
          <p className="text-sm text-slate-300">
            Switched off for everyone in Reminders:{" "}
            <span className="font-semibold">
              {data.global_off.map((k) => LABELS[k] || k).join(", ")}
            </span>
            . A team switch here cannot override that.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-slate-600 text-slate-300">Academy</Badge>
          <span className="text-xs text-slate-500">{academy.length} teams</span>
        </div>
        {academy.map(row)}
        {academy.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="team-notif-no-academy">
            No academy team matches that.
          </p>
        )}

        {section.length > 0 && (
          <>
            <div className="flex items-center gap-2 pt-4">
              <Badge variant="outline" className="border-slate-600 text-slate-300">
                Senior Section
              </Badge>
              <span className="text-xs text-slate-500">
                match reports belong to these sides
              </span>
            </div>
            {section.map(row)}
          </>
        )}
      </div>
    </Card>
  );
};

export default TeamNotifications;
